/**
 * HTML Template Agent Panel
 * AI-powered HTML template generation assistant as an inline panel for HTML Template nodes
 * Used as a tab in the NodeEditorModal left panel
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DroppableTextarea } from './DroppableTextarea';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  Bot,
  User,
  Send,
  Loader2,
  Copy,
  Check,
  Plus,
  Sparkles,
  ImagePlus,
  X,
} from 'lucide-react';
import { useIdentity } from '@/hooks/useIdentity';
import { useSubscription } from '@/contexts/SubscriptionContext';
import {
  chatWithHtmlTemplateAgent,
  extractHtmlCodeBlocks,
  type HtmlTemplateAgentMessage,
  type HtmlTemplateAgentInputNode,
  type HtmlTemplateAgentImage,
} from '@/services/htmlTemplateAgentApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface InputData {
  nodeId: string;
  nodeName: string;
  nodeType?: string;
  nodeDistance?: number;
  data?: any;
  output?: any;
  sourceHandle?: string;
}

// Export ChatMessage type for external state management
export interface HtmlTemplateChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  codeBlocks?: Array<{ language: string; code: string }>;
}

interface HtmlTemplateAgentPanelProps {
  inputData: InputData[];
  existingTemplate: string;
  primaryColor?: string;
  currencyCode?: string;
  dateFormat?: string;
  /** Output type - 'html' for web/PDF or 'email' for email-safe HTML */
  outputType?: 'html' | 'email';
  onInsertTemplate: (template: string) => void;
  // Optional external state management for conversation persistence across tab switches
  externalMessages?: HtmlTemplateChatMessage[];
  onMessagesChange?: (messages: HtmlTemplateChatMessage[]) => void;
}

export function HtmlTemplateAgentPanel({
  inputData,
  existingTemplate,
  primaryColor,
  currencyCode,
  dateFormat,
  outputType,
  onInsertTemplate,
  externalMessages,
  onMessagesChange,
}: HtmlTemplateAgentPanelProps) {
  const { getAccessToken } = useIdentity();
  const { subscriptionId } = useSubscription();

  // Use external state if provided, otherwise use internal state
  const [internalMessages, setInternalMessages] = useState<HtmlTemplateChatMessage[]>([]);
  const messages = externalMessages ?? internalMessages;

  // Helper to update messages that works with both internal and external state
  const updateMessages = useCallback((updater: HtmlTemplateChatMessage[] | ((prev: HtmlTemplateChatMessage[]) => HtmlTemplateChatMessage[])) => {
    if (onMessagesChange) {
      // External state - need to resolve the updater function manually
      if (typeof updater === 'function') {
        const currentMessages = externalMessages ?? [];
        onMessagesChange(updater(currentMessages));
      } else {
        onMessagesChange(updater);
      }
    } else {
      setInternalMessages(updater);
    }
  }, [onMessagesChange, externalMessages]);

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  // Image upload state
  const [uploadedImage, setUploadedImage] = useState<{
    dataUrl: string;
    data: string;
    mediaType: string;
    fileName: string;
  } | null>(null);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  // Focus textarea when panel mounts
  useEffect(() => {
    if (textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Handle image file selection
  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (PNG, JPEG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Image file is too large. Maximum size is 5MB.');
      return;
    }

    // Read file as data URL
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      // Extract base64 data without the data URL prefix
      const base64Data = dataUrl.split(',')[1];
      setUploadedImage({
        dataUrl,
        data: base64Data,
        mediaType: file.type,
        fileName: file.name,
      });
    };
    reader.readAsDataURL(file);

    // Reset the input so the same file can be selected again
    e.target.value = '';
  }, []);

  // Remove uploaded image
  const handleRemoveImage = useCallback(() => {
    setUploadedImage(null);
  }, []);

  const handleSend = useCallback(async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    // Add user message
    const userMessage: HtmlTemplateChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmedInput,
      timestamp: new Date(),
    };
    updateMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setStreamingContent('');

    // Build conversation history
    const conversationMessages: HtmlTemplateAgentMessage[] = [
      ...messages.map((m) => ({ role: m.role, content: m.content })),
      { role: 'user' as const, content: trimmedInput },
    ];

    // Build input schema from inputData
    // The inputData contains unified format { success, output, logs, metrics }
    // Extract the inner 'output' which is the actual data the user will access via {{data}}
    const inputSchema: HtmlTemplateAgentInputNode[] = inputData.map((d) => {
      // Get the unified format object
      const unified = d.data || d.output;
      // Extract the inner output (actual data) - this matches what {{data}} returns
      const actualOutput = unified?.output ?? unified;
      console.log('[HtmlTemplateAgent] Building input schema for node:', {
        nodeName: d.nodeName,
        hasData: !!d.data,
        hasOutput: !!d.output,
        unifiedType: typeof unified,
        actualOutputType: typeof actualOutput,
        actualOutputPreview: actualOutput ? JSON.stringify(actualOutput).slice(0, 200) : null,
      });
      return {
        nodeId: d.nodeId,
        nodeName: d.nodeName,
        sampleData: actualOutput,
      };
    });

    // Build reference image if uploaded
    const referenceImage: HtmlTemplateAgentImage | undefined = uploadedImage
      ? { data: uploadedImage.data, mediaType: uploadedImage.mediaType }
      : undefined;

    console.log('[HtmlTemplateAgent] Sending to API:', {
      inputSchemaLength: inputSchema.length,
      inputSchema: inputSchema.map(s => ({ nodeName: s.nodeName, hasSampleData: !!s.sampleData })),
      hasExistingTemplate: !!existingTemplate,
      hasReferenceImage: !!referenceImage,
    });

    // Clear the uploaded image after sending (it's now part of this message context)
    setUploadedImage(null);

    // Start streaming request
    abortControllerRef.current = chatWithHtmlTemplateAgent(
      {
        messages: conversationMessages,
        inputSchema,
        existingTemplate: existingTemplate || undefined,
        primaryColor,
        currencyCode,
        dateFormat,
        outputType,
        referenceImage,
      },
      {
        getAccessToken,
        subscriptionId: subscriptionId || undefined,
        onChunk: (chunk) => {
          setStreamingContent((prev) => prev + chunk);
        },
        onComplete: (fullResponse) => {
          // Extract code blocks from response
          const codeBlocks = extractHtmlCodeBlocks(fullResponse);

          // Add assistant message
          const assistantMessage: HtmlTemplateChatMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: fullResponse,
            timestamp: new Date(),
            codeBlocks,
          };
          updateMessages((prev) => [...prev, assistantMessage]);
          setStreamingContent('');
          setIsLoading(false);
        },
        onError: (error) => {
          // Add error as assistant message
          const errorMessage: HtmlTemplateChatMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: `Sorry, I encountered an error: ${error}`,
            timestamp: new Date(),
          };
          updateMessages((prev) => [...prev, errorMessage]);
          setStreamingContent('');
          setIsLoading(false);
        },
      }
    );
  }, [
    inputValue,
    isLoading,
    messages,
    inputData,
    existingTemplate,
    primaryColor,
    currencyCode,
    dateFormat,
    outputType,
    uploadedImage,
    getAccessToken,
    subscriptionId,
    updateMessages,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleCopyCode = useCallback(async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCodeId(id);
      setTimeout(() => setCopiedCodeId(null), 2000);
    } catch {
      // Ignore copy errors
    }
  }, []);

  const handleInsertCode = useCallback(
    (code: string) => {
      onInsertTemplate(code);
    },
    [onInsertTemplate]
  );

  // Detect dark mode
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const codeStyle = isDark ? vscDarkPlus : vs;

  return (
    <div className="flex flex-col h-full w-full bg-background min-w-0 overflow-hidden">
      {/* Messages Area */}
      <ScrollArea className="flex-1 w-full px-4 min-w-0" ref={scrollAreaRef}>
        <div className="py-4 space-y-4 min-w-0 w-full">
          {messages.length === 0 && !streamingContent && (
            <div className="text-center py-8">
              <Sparkles className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                Describe the HTML template you need
              </p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                I'll help you build HTML with your data fields
              </p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                <ImagePlus className="w-3.5 h-3.5 inline-block mr-1 align-text-bottom" />
                Upload a mockup or design image for visual reference
              </p>
            </div>
          )}

          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              codeStyle={codeStyle}
              copiedCodeId={copiedCodeId}
              onCopyCode={handleCopyCode}
              onInsertCode={handleInsertCode}
            />
          ))}

          {/* Streaming indicator */}
          {streamingContent && (
            <div className="flex gap-3 min-w-0 w-full">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <Bot className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0 w-0 rounded-lg bg-muted/50 px-3 py-2 overflow-hidden">
                <div className="prose prose-sm dark:prose-invert max-w-full break-words overflow-hidden [&>pre]:max-w-full [&>pre]:overflow-x-auto text-sm [&>p]:text-sm [&>p]:leading-relaxed [&>p]:my-2 [&>ul]:text-sm [&>ol]:text-sm [&>li]:text-sm [&>strong]:text-sm">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {streamingContent}
                  </ReactMarkdown>
                </div>
                <Loader2 className="w-4 h-4 animate-spin mt-2" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t p-4 flex-shrink-0">
        {/* Image Preview */}
        {uploadedImage && (
          <div className="mb-3 relative inline-block">
            <img
              src={uploadedImage.dataUrl}
              alt="Reference image"
              className="max-h-24 rounded-md border"
            />
            <Button
              variant="destructive"
              size="sm"
              className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full"
              onClick={handleRemoveImage}
            >
              <X className="w-3 h-3" />
            </Button>
            <p className="text-xs text-muted-foreground mt-1 truncate max-w-[200px]">
              {uploadedImage.fileName}
            </p>
          </div>
        )}

        <div className="flex gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={handleImageSelect}
            className="hidden"
          />

          {/* Image upload button */}
          <Button
            variant="outline"
            size="sm"
            className="self-end h-9 w-9 p-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            title="Upload reference image"
          >
            <ImagePlus className="w-4 h-4" />
          </Button>

          <DroppableTextarea
            ref={textareaRef}
            value={inputValue}
            onValueChange={setInputValue}
            onKeyDown={handleKeyDown}
            placeholder="Describe the template you need... (drag fields from Input Data)"
            className="min-h-[60px] max-h-[120px] resize-none text-sm"
            disabled={isLoading}
          />
          <Button
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            className="self-end h-9 w-9 p-0"
            size="sm"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Message Bubble Component
interface MessageBubbleProps {
  message: HtmlTemplateChatMessage;
  codeStyle: any;
  copiedCodeId: string | null;
  onCopyCode: (code: string, id: string) => void;
  onInsertCode: (code: string) => void;
}

function MessageBubble({
  message,
  codeStyle,
  copiedCodeId,
  onCopyCode,
  onInsertCode,
}: MessageBubbleProps) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="flex gap-3 justify-end">
        <div className="max-w-[85%] rounded-lg bg-primary text-primary-foreground px-3 py-2">
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
          <User className="w-4 h-4 text-primary-foreground" />
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex gap-3 min-w-0 w-full">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
        <Bot className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0 space-y-2 overflow-hidden w-0">
        <div className="rounded-lg bg-muted/50 px-3 py-2 w-full overflow-hidden">
          <div className="prose prose-sm dark:prose-invert max-w-full break-words overflow-hidden [&>pre]:max-w-full [&>pre]:overflow-x-auto text-sm [&>p]:text-sm [&>p]:leading-relaxed [&>p]:my-2 [&>ul]:text-sm [&>ol]:text-sm [&>li]:text-sm [&>strong]:text-sm">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Override pre to pass children through - prevents invalid <pre><div><button></div></pre> nesting
                pre({ children }) {
                  return <>{children}</>;
                },
                code({ className, children, ...props }: any) {
                  const content = String(children).replace(/\n$/, '');
                  const langMatch = /language-(\w+)/.exec(className || '');
                  const lang = langMatch ? langMatch[1] : '';

                  // Detect if this is a code block (has language) or inline code (no language, single line, short)
                  const isInlineCode = !lang && content.split('\n').length === 1 && content.length < 50;

                  if (isInlineCode) {
                    return (
                      <code className="px-1.5 py-0.5 bg-muted rounded text-[13px] font-mono">
                        {children}
                      </code>
                    );
                  }

                  const codeId = `${message.id}-${content.slice(0, 20)}`;

                  // Show Insert button for HTML and CSS code blocks
                  const isInsertableCode = (lang === 'html' || lang === 'css' || lang === 'handlebars') &&
                    content.split('\n').length > 1;

                  return (
                    <div className="my-3 rounded-md border min-w-0 w-full overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-1.5 bg-muted border-b flex-shrink-0">
                        <span className="text-xs font-medium text-muted-foreground uppercase">
                          {lang || 'code'}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => onCopyCode(content, codeId)}
                          >
                            {copiedCodeId === codeId ? (
                              <>
                                <Check className="w-3 h-3 mr-1" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3 mr-1" />
                                Copy
                              </>
                            )}
                          </Button>
                          {isInsertableCode && (
                            <Button
                              variant="default"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => onInsertCode(content)}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Insert
                            </Button>
                          )}
                        </div>
                      </div>
                      <SyntaxHighlighter
                        language={lang || 'html'}
                        style={codeStyle}
                        customStyle={{
                          margin: 0,
                          fontSize: '13px',
                          padding: '1rem',
                          overflowX: 'auto',
                          maxWidth: '100%',
                        }}
                        showLineNumbers={content.split('\n').length > 3}
                      >
                        {content}
                      </SyntaxHighlighter>
                    </div>
                  );
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}
